import { InputProps } from 'antd';
import { pipe } from 'ramda';
import React, { FC, useEffect, useMemo } from 'react';
import { Input } from 'components/atoms/input';
import {
  durationParser,
  getError,
  getMinutes,
  nonEmpty,
  useInputDuration,
  withGreaterThanZero,
  withZeroAsError,
} from 'components/atoms/input-duration';
import { withLessThanOneDayParser } from 'components/organisms/timesheets/common/utils';
import { Just, Nothing } from 'core/maybe';
import { useTranslation } from 'hooks/use-translation';
import { queueMacrotask } from 'utils/macrotask';

interface Props extends InputProps {
  minutes: number;
  onMinutesChange: (number) => void;
  onErrorStateChange?: (isError: boolean) => void;
}

export const TimeDurationInput: FC<Props> = ({
  minutes,
  onMinutesChange,
  onBlur,
  onErrorStateChange,
  onPressEnter,
  autoFocus,
  ...restProps
}) => {
  const parser = pipe(
    () => durationParser,
    withGreaterThanZero,
    withZeroAsError,
    withLessThanOneDayParser,
    nonEmpty,
  )();
  const { handleBlur, handleChange, handlePressEnter, value, state } =
    useInputDuration(minutes === 0 ? Nothing() : Just(minutes), parser);
  const duration = useMemo(() => {
    return state[0] === 'editing' ? state[2] : getMinutes(state);
  }, [state]);
  const error = useMemo(() => getError(state), [state]);

  const isError =
    error[0] === 'just' && error[1] !== 'zero' && error[1] !== 'empty';

  useEffect(() => {
    if (onErrorStateChange) {
      onErrorStateChange(isError);
    }
  }, [isError, onErrorStateChange]);

  const isZeroInput =
    error[0] === 'just' && (error[1] === 'zero' || error[1] === 'empty');

  const inputRef = React.useRef<any>(null);

  useEffect(() => {
    if (autoFocus) {
      queueMacrotask(() => {
        inputRef?.current?.focus();
      });
    }
  }, [autoFocus]);
  const updateToParent = () => {
    if (isError) {
      return;
    }
    // both minutes and duration are not present or same we will return
    if (!minutes && !duration[1]) {
      return;
    }
    if (minutes === duration[1]) {
      return;
    }
    onMinutesChange(isZeroInput ? 0 : duration[1]);
  };

  return (
    <Input
      ref={inputRef}
      autoFocus={autoFocus}
      value={value}
      onChange={handleChange}
      placeholder={useTranslation('timesheets.placeholders.time_tracked')}
      onPressEnter={(e) => {
        if (onPressEnter != null) {
          onPressEnter(e);

          return;
        }
        handlePressEnter(e);
        updateToParent();
      }}
      onFocus={() => {
        queueMacrotask(() => {
          inputRef?.current?.select();
        });
      }}
      onBlur={(e) => {
        handleBlur(e);
        updateToParent();
        onBlur?.(e);
      }}
      {...restProps}
    />
  );
};
